// src/features/my-shift/components/WeekList.tsx
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { eachDayOfInterval, format } from "date-fns";
import { todayInTimezone } from "@/features/shift/today";
import { formatLongDate, formatWeekdayShort } from "@/i18n/dateFormat";
import type { Shift } from "@/features/shift/types";
import { getShiftStartEnd } from "@/features/shift/types";
import { useShiftTypeLabel } from "@/features/shift/useShiftTypeLabel";
import { shiftColor } from "../shiftColors";
import { getWeekRange, formatTime12 } from "../calendar";
import { cn } from "@/lib/utils";

interface Props {
  anchor: Date;
  byDate: Record<string, Shift>;
  pendingDates: Set<string>;
  selectedDate: string | null;
  timezone: string;
  onSelectDate: (date: string) => void;
}

export function WeekList({ anchor, byDate, pendingDates, selectedDate, timezone, onSelectDate }: Props) {
  const { t, i18n } = useTranslation('shifts')
  const shiftTypeLabel = useShiftTypeLabel();
  const days = useMemo(() => {
    const { start, end } = getWeekRange(anchor);
    return eachDayOfInterval({ start: new Date(`${start}T12:00:00`), end: new Date(`${end}T12:00:00`) });
  }, [anchor]);
  const todayStr = todayInTimezone(timezone);

  return (
    <div className="flex flex-col gap-1.5">
      {days.map((d) => {
        const dateStr = format(d, "yyyy-MM-dd");
        const today = dateStr === todayStr;
        const selected = selectedDate === dateStr;
        const sch = byDate[dateStr];
        const code = sch?.shiftTypeCode || sch?.shift_type_code;
        const { startAt, endAt } = sch ? getShiftStartEnd(sch) : { startAt: "", endAt: "" };
        const color = shiftColor(code);
        const hasTime = code !== "off" && !!startAt && !!endAt;

        return (
          <button
            type="button" key={dateStr} onClick={() => onSelectDate(dateStr)}
            aria-label={formatLongDate(d, i18n.language)}
            aria-pressed={selected}
            className={cn(
              "flex items-center gap-3 rounded-lg border border-border bg-card px-3 py-2 text-left transition-colors hover:border-purple-300",
              today && "border-purple-600 ring-1 ring-purple-600",
              selected && "border-purple-600 ring-2 ring-purple-600",
            )}
          >
            <div className="w-11 flex-none text-center">
              <div className="text-[9px] font-bold uppercase text-muted-foreground">{formatWeekdayShort(d, i18n.language)}</div>
              <div className={cn("text-base font-extrabold", today ? "text-purple-700" : "text-muted-foreground")}>{format(d, "d")}</div>
            </div>
            <div className="w-1 self-stretch flex-none rounded-full" style={{ background: sch ? color.border : "hsl(var(--muted))" }} />
            <div className="min-w-0 flex-1">
              {sch ? (
                <>
                  <div className="text-[13px] font-bold" style={{ color: hasTime ? "hsl(var(--foreground))" : "hsl(var(--muted-foreground))" }}>
                    {hasTime ? (code ? shiftTypeLabel(code) : t('myShift.weekList.off')) : t('myShift.weekList.off')}
                  </div>
                  <div className="text-[11px] text-muted-foreground">
                    {hasTime ? `${formatTime12(startAt, timezone, i18n.language)} – ${formatTime12(endAt, timezone, i18n.language)}` : t('myShift.weekList.restDay')}
                  </div>
                </>
              ) : (
                <div className="text-[13px] font-semibold text-slate-300">{t('myShift.weekList.noShift')}</div>
              )}
            </div>
            {pendingDates.has(dateStr) && <span className="flex-none text-sm text-amber-500">⚡</span>}
            <span className="flex-none text-slate-300">›</span>
          </button>
        );
      })}
    </div>
  );
}
